import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useRenameWorkspace } from '../../../entities/workspace';
import { identityErrorMessage } from '../../../shared/api/identity-error';
import { validationMessage } from '../../../shared/i18n/zod';
import { Button } from '../../../shared/ui/button';
import { Field } from '../../../shared/ui/field';
import { Input } from '../../../shared/ui/input';
import { toast } from '../../../shared/ui/sonner';

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, validationMessage('orgNameRequired'))
    .max(80, validationMessage('orgNameMax')),
});
type Values = z.infer<typeof schema>;

/** Renames the workspace (`workspace.manage`). Disabled while archived: archived is read-only (Q55). */
export function RenameWorkspaceForm({
  workspace,
}: {
  workspace: { id: string; name: string; archived_at: string | null };
}) {
  const { t } = useTranslation('workspace');
  const rename = useRenameWorkspace(workspace.id);
  const archived = workspace.archived_at !== null;
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<Values>({
    resolver: zodResolver(schema),
    values: { name: workspace.name },
  });
  return (
    <form
      className="flex flex-wrap items-start gap-3"
      onSubmit={handleSubmit(async ({ name }) => {
        setError(null);
        try {
          await rename.mutateAsync(name);
          toast.success(t('settings.saved'));
        } catch (e) {
          setError(identityErrorMessage(e));
        }
      })}
    >
      <Field
        label={t('settings.name')}
        error={formState.errors.name?.message ?? error ?? undefined}
        {...(archived ? { description: t('settings.unarchiveHelp') } : {})}
        className="min-w-0 flex-1 basis-64"
      >
        <Input id="workspace-rename" autoComplete="off" disabled={archived} {...register('name')} />
      </Field>
      <Button
        type="submit"
        className="self-end sm:mt-7 sm:self-start"
        disabled={archived || formState.isSubmitting}
      >
        {t('settings.save')}
      </Button>
    </form>
  );
}
